/**
 * Creators list table — TanStack Table v8.
 * Click a row → drawer opens via `onSelect(user_id)`.
 */

import { useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';

import type { CreatorListItem } from './types';

interface CreatorsTableProps {
  data: CreatorListItem[];
  loading?: boolean;
  selectedId?: string | null;
  onSelect: (row: CreatorListItem) => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function stageLabel(stage: CreatorListItem['pipeline_stage']): string {
  switch (stage) {
    case 'incomplete':
      return 'Setup incomplete';
    case 'ready_to_submit':
      return 'Ready to submit';
    case 'in_review':
      return 'In review';
    default:
      return stage.charAt(0).toUpperCase() + stage.slice(1);
  }
}

function channelHint(channel: string | null): string | null {
  if (!channel) return null;
  switch (channel) {
    case 'x':
      return 'X';
    case 'email':
      return 'Email';
    case 'telegram':
      return 'Telegram';
    case 'whatsapp':
      return 'WhatsApp';
    default:
      return channel.charAt(0).toUpperCase() + channel.slice(1);
  }
}

export function CreatorsTable({ data, loading, selectedId, onSelect }: CreatorsTableProps) {
  const columns = useMemo<ColumnDef<CreatorListItem>[]>(
    () => [
      {
        accessorKey: 'updated_at',
        header: 'Updated',
        cell: ({ row }) => formatDate(row.original.updated_at),
      },
      {
        accessorKey: 'contact_name',
        header: 'Lead',
        cell: ({ row }) => (
          <div>
            <div className="font-medium text-foreground">
              {row.original.contact_name ?? row.original.display_name ?? 'Untitled lead'}
            </div>
            <div className="text-xs text-muted-foreground">
              {row.original.row_type === 'invite' ? 'Invite lead' : 'Creator profile'}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'contact_email',
        header: 'Contact',
        cell: ({ row }) => (
          <div className="space-y-1 text-muted-foreground">
            <div>{row.original.contact_email ?? '—'}</div>
            {row.original.contact_social ? (
              <a
                href={row.original.contact_social}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
                onClick={(event) => event.stopPropagation()}
              >
                {row.original.contact_social}
              </a>
            ) : null}
          </div>
        ),
      },
      {
        accessorKey: 'pipeline_stage',
        header: 'Stage',
        cell: ({ row }) => stageLabel(row.original.pipeline_stage),
      },
      {
        accessorKey: 'invite.last_contacted_at',
        header: 'Last contacted',
        cell: ({ row }) => {
          const lastAt = row.original.invite?.last_contacted_at ?? null;
          // outreach_channel can live on the row (creators API surfaces it on
          // invite rows directly) or, as a fallback, on the nested invite.
          const channel =
            row.original.outreach_channel ?? row.original.invite?.outreach_channel ?? null;
          const hint = channelHint(channel);
          return (
            <div>
              <div>{formatDate(lastAt)}</div>
              {hint ? <div className="text-xs text-muted-foreground">via {hint}</div> : null}
            </div>
          );
        },
      },
      {
        accessorKey: 'invite.next_follow_up_at',
        header: 'Next follow-up',
        cell: ({ row }) => formatDate(row.original.invite?.next_follow_up_at ?? null),
      },
      {
        accessorKey: 'claim_url',
        header: 'Claim link',
        cell: ({ row }) =>
          row.original.claim_url ? (
            <button
              type="button"
              className="font-mono text-xs text-primary hover:underline"
              onClick={(event) => {
                event.stopPropagation();
                navigator.clipboard?.writeText(row.original.claim_url ?? '');
              }}
            >
              Copy link
            </button>
          ) : (
            '—'
          ),
      },
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="overflow-x-auto rounded-md border bg-background">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((header) => (
                <th key={header.id} className="px-4 py-3 font-medium">
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-12 text-center text-muted-foreground">
                Loading…
              </td>
            </tr>
          ) : table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-12 text-center text-muted-foreground">
                No creators found.
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => {
              const isSelected = row.original.row_id === selectedId;
              return (
                <tr
                  key={row.id}
                  className={`cursor-pointer border-t transition-colors hover:bg-muted/40 ${
                    isSelected ? 'bg-muted/60' : ''
                  }`}
                  onClick={() => onSelect(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
